import type { FinderData, LocationGroup } from './types';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '');
}

const RETRY_DELAYS_MS = [1500, 3000];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/**
 * GET with two retries on a 5xx or network error: this runs on a public page,
 * and a brief edge hiccup should not show visitors an error card.
 */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    try {
      const res = await fetch(url, { headers: headers(), signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new Error(`${url} responded ${res.status}`);
      if (res.status < 500) break; // a 4xx will not fix itself
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

/** Every public event in the window, series collapsed, plus filter options. */
export function fetchFinderData(
  apiBaseUrl: string,
  days: number | null,
  signal?: AbortSignal
): Promise<FinderData> {
  const qs = days ? `?days=${days}` : '';
  return getJson<FinderData>(`${base(apiBaseUrl)}/api/events/finder${qs}`, signal);
}

/** Campuses grouped by location, with each location's brand SVG. */
export async function fetchLocations(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<LocationGroup[]> {
  const data = await getJson<unknown>(`${base(apiBaseUrl)}/api/locations`, signal);
  // An error body ({ error }) must not take the finder down with it.
  return Array.isArray(data) ? (data as LocationGroup[]) : [];
}

/**
 * The signed-in visitor's default campus (Contacts.Web_Congregation_ID,
 * falling back to their household's campus). Null when not signed in.
 */
export async function fetchHomeCampus(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<number | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/user/home-campus`, {
      headers: { ...headers(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { congregationId: number | null };
    return data.congregationId ?? null;
  } catch {
    return null;
  }
}

/** Persist a campus choice to MP for the signed-in visitor. No-op when signed out. */
export async function setHomeCampus(apiBaseUrl: string, congregationId: number): Promise<boolean> {
  const token = getMpWidgetToken();
  if (!token) return false;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/set-home-campus`, {
      method: 'POST',
      headers: {
        ...headers(),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ congregationId }),
    });
    return res.ok;
  } catch (err) {
    console.error('Event finder: could not save home campus', err);
    return false;
  }
}
